/**
 * chartCategoryLabels.ts — what a bar-family chart labels each row with
 * (#1808).
 *
 * The Sky BIPs tables put the statement in a side-group heading and the brand
 * in the row label, so a chart of them said "Sky" on every bar. The widget's
 * `category_label` setting relabels the rows from the side groups before the
 * chart sees the table:
 *
 *   'row'        the row label (absent means this — today's behaviour)
 *   'group_row'  innermost heading, spaced en dash, row label
 *                ("Good value for money – Sky")
 *   'group'      the innermost heading only
 *
 * A row no group covers keeps its own label. Pure: no React, no fetch.
 */

import type { ChartCategoryLabelMode } from '../types/gotypes'
import type { PlatinumData } from '../types/carbon/models/PlatinumData'
import { innermostSideGroupLabels } from './tableViewConfig'
import { applySelectionToPlatinumData, rowsAsPositions, type ResolvedSelection } from './tableSelection'

/** Separator between heading and row label: an en dash with spaces. */
export const CATEGORY_LABEL_SEPARATOR = ' – '

/** Relabels the side vecs from their innermost side-group heading. Groups are kept. */
export function applyCategoryLabelMode(
  data: PlatinumData,
  mode: ChartCategoryLabelMode | undefined,
): PlatinumData {
  if (!mode || mode === 'row') return data
  const headings = innermostSideGroupLabels(data)
  if (headings.every((h) => h === undefined)) return data
  const vecs = (data.side?.vecs || []).map((vec, index) => {
    const heading = headings[index]
    if (!heading) return vec
    const label = mode === 'group' ? heading : `${heading}${CATEGORY_LABEL_SEPARATOR}${vec.label || ''}`
    return { ...vec, label }
  })
  return { ...data, side: { ...data.side, vecs } }
}

/**
 * The chart's view of the table: relabelled by `mode`, then cut to the
 * selection. The relabel runs FIRST because the selection step drops the side
 * groups the headings come from. A saved name-based selection is pinned to
 * positions in the unlabelled table first, so it still finds its rows.
 * With no mode this is exactly `applySelectionToPlatinumData`.
 */
export function chartDataWithCategoryLabels(
  data: PlatinumData,
  resolved: ResolvedSelection,
  mode: ChartCategoryLabelMode | undefined,
): PlatinumData {
  if (!mode || mode === 'row') return applySelectionToPlatinumData(data, resolved)
  return applySelectionToPlatinumData(applyCategoryLabelMode(data, mode), rowsAsPositions(resolved, data))
}
